import { Button, Checkbox, Modal } from "@bp/ui-components";
import { useState } from "react";
import { useMutation } from "urql";
import { graphql } from "../../../client/gql";
import { MODULE_CATALOG, type ModuleProduct } from "../../../utils/modules";
import { showSuccessToast, showUserErrorToast } from "../../../utils/toast";
import css from "../orgList.module.scss";

const ModuleEditModalUpdateApplication = graphql(`
  mutation ModuleEditModal_UpdateApplication(
    $where: ApplicationWhere!
    $update: ApplicationUpdateInput!
  ) {
    updateApplications(where: $where, update: $update) {
      applications {
        uuid
      }
    }
  }
`);

type Props = {
  org: { uuid: string; name: string };
  product: ModuleProduct;
  current: readonly (string | null)[];
  onClose: () => void;
  onSaved: () => void;
};

export function ModuleEditModal({ org, product, current, onClose, onSaved }: Props) {
  const cat = MODULE_CATALOG[product];
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(current.filter((m): m is string => Boolean(m))),
  );
  const [busy, setBusy] = useState(false);
  const [, updateApp] = useMutation(ModuleEditModalUpdateApplication);

  const toggle = (v: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(v)) {
        n.delete(v);
      } else {
        n.add(v);
      }
      return n;
    });

  const save = async () => {
    setBusy(true);
    // Update the license-edge properties from the application side (the only
    // mutable direction): applications(clientId) → licensedOrganizations(org) → edge.
    const res = await updateApp({
      where: { clientId: cat.clientId },
      update: {
        licensedOrganizations: [
          {
            where: { node: { uuid: org.uuid } },
            update: { edge: { licensedModules: [...selected] } },
          },
        ],
      },
    });
    setBusy(false);
    if (res.error) {
      showUserErrorToast({ text: "Speichern fehlgeschlagen", error: res.error });
      return;
    }
    showSuccessToast(`${cat.label}-Module gespeichert`);
    onSaved();
  };

  return (
    <Modal
      isOpen
      ariaHideApp={false}
      hideFooter={false}
      width="s"
      title={`${cat.label}-Module — ${org.name}`}
      onRequestClose={onClose}
      footer={
        <div className={css.foot}>
          <span className={css.footHint}>Freigeschaltete Module dieses Produkts.</span>
          <div className={css.footActions}>
            <Button hierarchy="tertiary" onClick={onClose}>
              Abbrechen
            </Button>
            <Button hierarchy="primary" disabled={busy} onClick={() => void save()}>
              Speichern
            </Button>
          </div>
        </div>
      }
    >
      <div className={css.stack}>
        {cat.modules.map((m) => (
          <Checkbox
            key={m.value}
            name={m.value}
            label={m.label}
            checked={selected.has(m.value)}
            onChange={() => toggle(m.value)}
          />
        ))}
      </div>
    </Modal>
  );
}
